import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { getActivitiesByCategory } from '@/mocks/activities';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'articles' | 'activities'>('articles');
  const [activeCategory, setActiveCategory] = useState(slug || 'outdoor');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const category = getCategoryBySlug(activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);
  const categoryActivities = getActivitiesByCategory(activeCategory);

  const handleCategorySwitch = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <PageSEO
        title={category ? `${category.name} - 活动分类` : '活动分类'}
        description={category ? `${category.description}。来17c一起发现更多${category.name}的精彩活动与文章。` : '浏览17c一起所有兴趣分类，发现志同道合的伙伴。'}
        keywords={`17c一起${category ? category.name : ''}, 17c一起活动, 17c一起社交`}
        canonicalUrl={`https://17cyiqi.com/category/${activeCategory}`}
      />
      <ScrollToTop />

      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '活动分类' },
                ...(category ? [{ label: category.name }] : []),
              ]}
            />

            <div className="pb-8 md:pb-12">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                {category ? category.name : '活动分类'}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {category ? category.description : '浏览所有兴趣分类'}
              </p>
            </div>

            <div className="pb-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySwitch(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-label whitespace-nowrap transition-colors cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-1 pb-8 border-b border-background-200/70">
              <button
                onClick={() => setActiveTab('articles')}
                className={`px-6 py-2.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === 'articles'
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                文章 ({categoryArticles.length})
              </button>
              <button
                onClick={() => setActiveTab('activities')}
                className={`px-6 py-2.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === 'activities'
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                活动 ({categoryActivities.length})
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'articles' && (
          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors group cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-label font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i> {article.readTime}
                          </span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-400 text-sm">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'activities' && (
          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              {categoryActivities.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {categoryActivities.map((activity) => (
                    <div
                      key={activity.id}
                      className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors group cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={activity.image}
                          alt={activity.title}
                          title={activity.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="bg-accent-500 text-background-50 text-xs px-2.5 py-1 rounded-full font-label whitespace-nowrap">
                            {activity.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-label font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug">
                          {activity.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-3">
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-calendar-line"></i> {activity.date}
                          </span>
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-map-pin-line"></i> {activity.location}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-background-200/70">
                          <span className="text-xs text-foreground-400 whitespace-nowrap">
                            {activity.participants}/{activity.maxParticipants}人
                          </span>
                          <span className="text-xs text-primary-500 font-label whitespace-nowrap">
                            查看详情
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-400 text-sm">该分类暂无活动</p>
                </div>
              )}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}